// Picks the browser softphone and links the calls it places to call actions.
//   features.softphone (comms.softphone) with telephony ringcentral or simulator: the phone (client.ts), one per browser
//   otherwise: no softphone; the server drives a simulated call, or the operator dials a desk phone
//
// Linking (RingCentral): when the console places a call it registers the pending dial (event, action, number). When the
// phone reports the call started with a telephonySessionId, the matching dial is reported to the server
// (POST /api/events/:id/calls/:actionId/session). The server also matches calls by extension, number and time from
// RingCentral's webhooks, so a missed report does not lose the call. A simulated call is placed and linked by the server.

import { api, errorText } from '../api.ts';
import type { AppConfig } from '../auth.ts';
import { PhoneClient } from './client.ts';
import { phoneKey, type Softphone } from './softphone.ts';

export type { PhoneCall, PhoneState, Softphone, SoftphoneEvent, SoftphoneState } from './softphone.ts';
export { useEventInView, useMarkEventInView, useSoftphoneState } from './softphone.ts';

interface PendingDial {
  eventId: string;
  actionId: string;
  key: string;
  at: number;
}

const PENDING_MS = 2 * 60_000;
let instance: Softphone | null = null;
let pending: PendingDial[] = [];

/** The softphone for this deployment, created once; null without comms.softphone, and for manual dialing. */
export function softphoneFor(cfg: Pick<AppConfig, 'telephony' | 'features'>): Softphone | null {
  const provider = cfg.telephony.provider;
  if (!cfg.features.softphone || (provider !== 'ringcentral' && provider !== 'simulator')) return null;
  if (!instance) {
    const sp = new PhoneClient(provider);
    if (provider === 'ringcentral') {
      sp.on((e) => {
        if (e.type !== 'started' || e.call.direction === 'inbound' || !e.call.telephonySessionId) return;
        const tsid = e.call.telephonySessionId;
        const now = Date.now();
        pending = pending.filter((p) => now - p.at < PENDING_MS);
        const key = phoneKey(e.call.to);
        // Prefer the dial to the same number; otherwise the most recent one.
        const match = (key && [...pending].reverse().find((p) => p.key === key)) || pending[pending.length - 1];
        if (!match) return;
        pending = pending.filter((p) => p !== match);
        api.callSession(match.eventId, match.actionId, tsid).catch((err: unknown) => {
          console.warn(`Could not link RingCentral session ${tsid} to call ${match.actionId}: ${errorText(err)}`);
        });
      });
    }
    instance = sp;
  }
  return instance;
}

/** Call before asking the softphone to dial, so the started call can be linked to its action. */
export function expectCall(p: { eventId: string; actionId: string; e164: string }): void {
  pending.push({ eventId: p.eventId, actionId: p.actionId, key: phoneKey(p.e164), at: Date.now() });
}
